import { ExternalLink, Github } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Project } from "@/types/supabase";

const ProjectCard = ({
  project,
  index,
  showHighlights,
}: {
  project: Project;
  index: number;
  showHighlights: boolean;
}) => {
  return (
    <div
      key={project.title}
      className="glass-morphism rounded-xl overflow-hidden animate-fade-in"
      style={{ animationDelay: `${0.2 + index * 0.1}s` }}
    >
      <div className="p-6 flex flex-col">
        <h2 className="text-xl font-semibold text-white mb-3">{project.title}</h2>
        {/* Image below title, above content */}
        {showHighlights ? (
          <img
            src={project.image_url}
            alt={project.title}
            className="w-full h-auto object-contain rounded-xl mb-6"
            loading="lazy"
          />
        ) : (
          <div className="relative h-48 overflow-hidden mb-6">
            <div className="absolute inset-0 bg-gradient-to-t from-navy to-transparent opacity-70"></div>
            <img
              src={project.image_url}
              alt={project.title}
              className="w-full h-full object-cover rounded-xl"
              width="400"
              height="192"
              loading="lazy"
            />
          </div>
        )}

        {project.description && (
          showHighlights ? (
            <div
              className="text-gray-400 mb-4"
              dangerouslySetInnerHTML={{ __html: project.description }}
            />
          ) : (
            <div className="text-gray-400 mb-4">
              {project.description.replace(/<[^>]+>/g, "")}
            </div>
          )
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          {project.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="text-xs bg-white/10 text-gray-300 px-2 py-1 rounded-full"
            >
              {tag}
            </span>
          ))}
          {project.tags.length > 3 && (
            <span className="text-xs text-gray-400 flex items-center px-1">
              +{project.tags.length - 3} similar
            </span>
          )}
        </div>

        {showHighlights && (
          <div className="space-y-2 mb-5">
            {project.highlights.map((highlight, idx) => (
              <div key={idx} className="flex items-center">
                <div className="h-1.5 w-1.5 rounded-full bg-teal mr-2.5 ml-0.5"></div>
                <span className="text-sm text-gray-300">{highlight}</span>
              </div>
            ))}
          </div>
        )}

        {((project.github_url ?? null) || (project.demo_url ?? null)) && (() => {
          const hasGithub = !!project.github_url;
          const hasDemo = !!project.demo_url;
          const twoButtons = hasGithub && hasDemo;
          const btnClasses = "border-teal/30 hover:border-teal/80 bg-transparent text-white hover:bg-teal/10";

          return (
            <div className={cn("flex flex-row gap-2", twoButtons ? "justify-between" : "justify-end")}> 
              {hasGithub && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(btnClasses, twoButtons ? "w-[50%]" : "")}
                  asChild
                >
                  <a
                    href={project.github_url as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-full hover:bg-gray-800/50 transition-colors flex flex-row gap-1 items-center"
                  >
                    <Github className="h-4 w-4 text-gray-400 hover:text-white" />{" "}
                    Github
                  </a>
                </Button>
              )}

              {hasDemo && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(btnClasses, twoButtons ? "w-[50%]" : "")}
                  asChild
                >
                  <a
                    href={project.demo_url as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-full hover:bg-gray-800/50 transition-colors flex flex-row gap-1 items-center"
                  >
                    <ExternalLink className="h-4 w-4 text-gray-400 hover:text-white" />{" "}
                    {(/youtube\.com|youtu\.be/i.test(project.demo_url as string) ? "YouTube" : "Live Demo")}
                  </a>
                </Button>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ProjectCard;
